import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection, query, orderBy, onSnapshot, addDoc, doc,
  serverTimestamp, setDoc, getDoc,getDocs, where,    
} from 'firebase/firestore';
import { db } from '../../firebase';
import './ClientMessages.css';
import { buildFunctionUrl } from '../../config/functions';
import { sendNotification } from '../../utils/sendNotification';

const ClientMessages = () => {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [activeProjectId, setActiveProjectId] = useState(null);
  

  useEffect(() => {
  const user = getAuth().currentUser;
  if (user) {
    // ⛔ setClientId(user.uid);
    setClientId(user.email.toLowerCase()); // ✅ igual al usado en admin
  }
}, []);


 // ✅ 1. Obtener el email del cliente desde Firestore
useEffect(() => {
  const fetchEmail = async () => {
    if (!clientId) return;
    try {
      const ref = doc(db, 'clients', clientId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setEmail(snap.data().email);
      }
    } catch (err) {
      console.error('❌ Error al cargar email del cliente:', err);
      setStatus('error');
    }
  };

  fetchEmail();
}, [clientId]);


// ✅ 2. Obtener proyecto ACTIVO del cliente (NO ANIDADO)
useEffect(() => {
  const fetchClientAndProject = async () => {
    const user = getAuth().currentUser;
    if (!user) return;

    try {
      const clientEmail = user.email.toLowerCase();

      // buscamos al cliente por email
      const clientSnap = await getDocs(
        query(collection(db, "clients"), where("email", "==", clientEmail))
      );

      if (clientSnap.empty) return;

      const clientDoc = clientSnap.docs[0];
      setClientId(clientDoc.id); // ✅ ahora clientId será el UID de Firestore

      // buscamos proyecto activo
      const projectsSnap = await getDocs(collection(db, "clients", clientDoc.id, "projects"));
      const activeProject = projectsSnap.docs.find(
        (p) => p.data().estado === "activo"
      );

      if (activeProject) {
        setActiveProjectId(activeProject.id);
      }

    } catch (err) {
      console.error("❌ Error al obtener proyecto activo:", err);
    }
  };

  fetchClientAndProject();
}, []);     // 👈 SOLO CORRE UNA VEZ AL MONTAR EL COMPONENTE

useEffect(() => {
  if (!clientId) return;

  const q = query(
    collection(db, 'conversations', clientId, 'messages'),
    orderBy('timestamp')
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() })); // 👈 aquí cambiamos "doc" por "d"
    setMessages(msgs);
  });

  return () => unsubscribe();
}, [clientId]);


  const formatPhoneForSms = (value = '') => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('1') && digits.length === 11) {
      return `+${digits}`;
    }
    if (digits.length === 10) return `+1${digits}`;
    return value.startsWith('+') ? value : `+${digits}`;
  };

  const notifyAdminViaSms = async (subjectLine, bodyText) => {
    const adminPhone = '+17872369189';
    const formatted = formatPhoneForSms(adminPhone);
    const preview = `Lead ${email || clientId}: ${subjectLine || ''} ${bodyText || ''}`.trim().slice(0, 150);
    try {
      await sendNotification('sms', {
        to: formatted,
        message: preview || 'Nuevo mensaje en el portal PRWD',
      });
    } catch (err) {
      console.warn('⚠️ No se pudo enviar SMS al admin:', err.message || err);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      // ✅ 1. Enviar el correo
      const res = await fetch(buildFunctionUrl('/sendClientEmail'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
        to: 'info@puertoricowindows.com',
          subject,
          text: body,
          html: `<p>${body}</p>`,
          attachments: attachments || [],
        }),
      });
  
      if (!res.ok) throw new Error('Fallo el envío del email');
  
      // ✅ 2. Guardar mensaje
      const convoRef = doc(db, 'conversations', clientId);
      await setDoc(convoRef, { updatedAt: serverTimestamp() }, { merge: true });
  
      const msgRef = collection(db, 'conversations', clientId, 'messages');
      await addDoc(msgRef, {
        from: 'client',
        subject,
        body,
        timestamp: serverTimestamp(),
        attachments: attachments || [],
        read: false,
      });
  
      // ✅ 3. Buscar proyecto activo (si hay)
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);
      const clientData = clientSnap.exists() ? clientSnap.data() : null;
  
      if (clientData && clientData.uid) {
        const projectsRef = collection(db, 'clients', clientId, 'projects');
        const projectsSnap = await getDocs(projectsRef);
        const activeProject = projectsSnap.docs.find(doc => doc.data().estado === 'activo');
  
        if (activeProject) {
          const timelineRef = doc(db, 'clients', clientId, 'projects', activeProject.id);
          const projectData = activeProject.data();
  
          const newEvent = {
            title: '✉️ Mensaje enviado por el cliente',
            description: `${subject} — ${body}`,
            color: 'gray',
            timestamp: new Date(), // usa Date en lugar de serverTimestamp aquí
          };
  
          const currentTimeline = projectData.projectTimeline || [];
          const updatedTimeline = [...currentTimeline, newEvent];
  
          await setDoc(timelineRef, { projectTimeline: updatedTimeline }, { merge: true });
        }
      }
  
      // ✅ Limpiar inputs
      setSubject('');

      await notifyAdminViaSms(subject, body);

      setBody('');
      setAttachments([]);
      setStatus('sent');
  
    } catch (err) {
      console.error('❌ Error al enviar o guardar mensaje:', err);
      setStatus('error');
    }
  };
  
  

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    const promises = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          content: reader.result.split(',')[1],
          filename: file.name,
          type: file.type,
        });
      };
      reader.readAsDataURL(file);
    }));
    Promise.all(promises).then(setAttachments);
  };

  return (
    <div className="email-container">
      <h3>Mensajería con el administrador</h3>
  
      <div className="chat-box">
        <div className="chat-history">
          {messages.map(msg => (
            <div key={msg.id} className={`message ${msg.from === 'client' ? 'sent' : 'received'}`}>
              <div>
                <strong>{msg.from}</strong> • {msg.timestamp?.toDate().toLocaleString()}
              </div>
              <div>{msg.body}</div>
              {msg.attachments?.map((a, i) => (
                <div key={i}>
                  <a href={`data:${a.type};base64,${a.content}`} download={a.filename}>
                    {a.filename}
                  </a>
                </div>
              ))}
            </div>
          ))}
        </div>
  
        <form className="chat-input" onSubmit={sendMessage}>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Asunto"
            required
            
          />
  
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Escribe tu mensaje…"
            required
            
          />
  
          <input type="file" multiple onChange={handleFiles} />
  
          <button
  type="submit"
  disabled={!subject || !body || status === 'sending'}
>
  {status === 'sending' ? 'Enviando…' : 'Enviar'}
</button>

  
          {status === 'sent' && <p style={{ color: 'green' }}>Mensaje enviado ✅</p>}
          {status === 'error' && <p style={{ color: 'red' }}>Error al enviar mensaje ❌</p>}
        </form>
      </div>
    </div>

  );
  
};

export default ClientMessages;
